import { useState, Dispatch, SetStateAction } from 'react';
import { first } from 'rxjs/operators';
import { IDataPackage } from '@vivli/shared/features/data-package/infrastructure/interface';
import { useDataPackageApiContext, useDataRequestsApiContext, useModalApiContext } from '@vivli/core/infrastructure/context';

export const useResultRequestsFileInspection = (
    onPackageSubmitted?: (pkg: IDataPackage) => void,
    externalSetSubmittingDataPackage?: Dispatch<SetStateAction<boolean>>
) => {
    const _dataPackageService = useDataPackageApiContext();
    const _dataRequestService = useDataRequestsApiContext();
    const _modalService = useModalApiContext();

    const [submittingDataPackage, setSubmittingDataPackage] = useState(false);

    const handleRestrictedFilesFound = (badFiles?: string[], forZipFiles?: boolean) => {
        const badFileTypes = getBadFileTypes(badFiles);
        const msg = forZipFiles
            ? `For data protection and security reasons, certain file types cannot be exported. This ZIP archive includes one or more restricted files and cannot be exported as-is.\r\n Please remove the restricted ${badFileTypes} file(s) and re-attempt export.`
            : `For data protection and security reasons, certain file types cannot be exported. Please remove the restricted ${badFileTypes} file(s) and re-attempt export.`;
        _modalService.message(msg, {
            title: 'Export Not Allowed – Restricted File Type',
            showContinueButton: true
        });
    };

    const getBadFileTypes = (badFiles?: string[]): string[] => {
        if (!badFiles || badFiles.length === 0) return [];
        return badFiles.map((f) => {
            const ext = f.split('.').pop();
            return ext ? `.${ext}` : '';
        });
    };

    const confirmAndSubmit = (dataRequestId: string, dataPackage: IDataPackage, resultRequestId: string) => {
        _modalService.confirm('Are you sure all files have been uploaded? This action cannot be undone.', {
            title: 'Submit Files?',
            onConfirm: () => {
                setSubmittingDataPackage(true);
                if (externalSetSubmittingDataPackage) externalSetSubmittingDataPackage(true);

                _dataRequestService
                    .startHeuristics(dataRequestId, resultRequestId, dataPackage)
                    .pipe(first())
                    .subscribe(
                        () => {
                            setSubmittingDataPackage(false);
                            if (externalSetSubmittingDataPackage) externalSetSubmittingDataPackage(false);
                            onPackageSubmitted && onPackageSubmitted(dataPackage);
                        },
                        (error) => {
                            setSubmittingDataPackage(false);
                            if (externalSetSubmittingDataPackage) externalSetSubmittingDataPackage(false);
                            _dataPackageService.handleSubmitErrorWithFileCheck(error, true, 'Result Request');
                        }
                    );
            }
        });
    };

    return {
        confirmAndSubmit,
        handleRestrictedFilesFound,
        submittingDataPackage
    };
};
